'use client';

import { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import NeuralRunner from '@/components/games/NeuralRunner';
import { apiClient } from '@/lib/api';
import { getAccessToken } from '@/lib/authStorage';

type LeaderboardEntry = { name: string; score: number; avatar?: string | null; date?: string };

// Анонимный ID для гостей
function getAnonId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const key = 'neural_runner_anon_id';
    let v = localStorage.getItem(key) || '';
    if (!v) {
      const uuid = (globalThis.crypto as any)?.randomUUID?.();
      v = typeof uuid === 'string' && uuid ? uuid : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(key, v);
    }
    return v.slice(0, 64);
  } catch { return ''; }
}

export default function NeuralRunnerPage() {
  const router = useRouter();
  const [myBest, setMyBest] = useState(0);
  const [globalBest, setGlobalBest] = useState(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);

  // Загрузка рекордов и лидерборда
  const loadData = useCallback(async () => {
    try {
      const anon = getAnonId();
      const qs = anon ? `?anon_id=${encodeURIComponent(anon)}` : '';
      const [bestData, lbData] = await Promise.all([
        apiClient.request<{ global_best?: number; my_best?: number }>(`/api/games/neural-runner/best/${qs}`),
        apiClient.request<{ leaderboard?: LeaderboardEntry[] }>(`/api/games/neural-runner/leaderboard/?limit=10`),
      ]);
      setGlobalBest(Number(bestData?.global_best) || 0);
      setMyBest(Number(bestData?.my_best) || 0);
      setLeaderboard(lbData?.leaderboard || []);
    } catch {
      // Не критично
    }
  }, []);

  useEffect(() => { void loadData(); }, [loadData]);

  // Отправка счёта
  const submitScore = useCallback(async (score: number) => {
    try {
      const anon = getAnonId();
      const data = await apiClient.request<{ global_best?: number; my_best?: number }>(
        `/api/games/neural-runner/score/`,
        { method: 'POST', body: JSON.stringify({ score, anon_id: anon || undefined }) },
      );
      if (data?.global_best) setGlobalBest(g => Math.max(g, Number(data.global_best)));
      if (data?.my_best) setMyBest(m => Math.max(m, Number(data.my_best)));
      // Обновляем лидерборд
      void loadData();
    } catch {
      // Сеть/сервер недоступны — не мешаем
    }
  }, [loadData]);

  return (
    <div className="flex flex-col h-screen bg-site-bg overflow-hidden">
      {/* Шапка */}
      <SiteHeader />

      {/* Игра занимает всё оставшееся пространство */}
      <main className="flex-1 relative min-h-0">
        <NeuralRunner
          onExit={() => router.push('/games')}
          onScoreSubmit={submitScore}
          leaderboard={leaderboard}
          myBest={myBest}
          globalBest={globalBest}
        />
      </main>
    </div>
  );
}
